import { Fragment, ReactElement, useEffect, useState } from "react";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import AxisHorizontal from "../../../../components/charts/AxisHorizontal";
import AxisVertical from "../../../../components/charts/AxisVertical";
import Grid from "../../../../components/charts/grid";
import Chart from "../../../../components/charts/chart";
import { IAxisLinear, IPoint, ISeriesWithAxes } from "../../../../components/charts/types";
import * as d3 from "d3";
import { IMillProductDetail } from "../../types";
import CircularProgress from "@mui/material/CircularProgress";
import { formatDateTimeWithSeconds, formatNumber, formatTimeWithSeconds } from "../../../../system/utils";
import { useLazyFetch } from "../../../../system/useFetch";
import { IProductionStage } from "../../../../types";
import { Alert } from "@mui/material";
import KeyValueItem from "../../../../components/charts/keyValueItem";
import HtmlTooltip from "../../../../components/charts/tooltip";
import { getPalette } from "../../../../configuration/configuration";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Legend from "../../../../components/charts/legend";

interface IProps {
  productDetail: IMillProductDetail;
}

export interface IChartConfig {
  series: ISeriesWithAxes[];
  xAxis: IAxisLinear;
  yAxes: IAxisLinear[];
}


const ProductTrends = ({ productDetail }: IProps): ReactElement => {
  const lineID = useUniqueId("product-detail-trends");

  const [ showAxes, setShowAxes ] = useState<boolean>(false);
  const[ lineChartConfig, setLineChartConfig ] = useState<IChartConfig | null>(null);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 300,
    marginBottom: 50,
    marginLeft: 10,
    marginRight: showAxes && lineChartConfig ? lineChartConfig.yAxes.length * 70 : 10,
    marginTop: 30
  });
  const [getTrends, { data }] = useLazyFetch<IProductionStage[]>();

  useEffect(() => {
    getTrends({
      module: "mill",
      query: "getMillProductTrendsID",
      payload: {
        productionDetailID: productDetail.productionDetailID
      }
    });
  }, [productDetail]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const series: ISeriesWithAxes[] = [];
    const yAxes: IAxisLinear[] = [];
    let minimum = Number.MAX_VALUE;
    let maximum = Number.MIN_VALUE;


    data.forEach(stage => {

      stage.series.map(item => {
        const yAxisName = `${item.axis.name} ${item.axis.unit ? `[${item.axis.unit}]` : ""}`;
        const xs = item.data.map(p => p.x);

        minimum = Math.min(minimum, ...xs);
        maximum = Math.max(maximum, ...xs);

        if (!yAxes.find(y => y.name === yAxisName)){
          yAxes.push({
            name: yAxisName,
            minimum: item.axis.minimum ?? 0,
            maximum: item.axis.maximum ?? 100,
            ticksCount: 3,
            formatTick: (value) => (value as number).toFixed(2)
          });
        }

        series.push({
          name: item.name,
          xAxisName: "Time",
          yAxisName,
          value: item.data as IPoint[],
          color: item.color ?? getPalette().list[series.length],
          unit: item.axis.unit ?? undefined,
          type: "line",
          limits: item.limits
        });
      });
    });


    // series.forEach(s => {
    //   (s.value as IPoint[]).forEach(p => {
    //     minimum = Math.min(minimum, p.x as number);
    //     maximum = Math.max(maximum, p.x as number);
    //   });
    // });


    const config: IChartConfig = {
      xAxis: {
        name: "Time",
        minimum,
        maximum,
        ticksCount: 5,
        formatTick: (value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)
      },
      series,
      yAxes
    };

    setLineChartConfig(config);

  }, [productDetail, data]);

  if (!lineChartConfig) {
    return <div className="text-center"><CircularProgress /></div>;
  }

  if (lineChartConfig.series.length === 0) {
    return <Alert severity="warning">No Data to Display.</Alert>;
  }


  const xScale = d3.scaleLinear()
    .domain([lineChartConfig.xAxis.minimum, lineChartConfig.xAxis.maximum])
    .range([0, dimensions.boundedWidth]);

  const yScales = lineChartConfig.yAxes.map(item => ({
    name: item.name,
    config: item,
    axis: d3.scaleLinear()
      .domain([item.minimum ?? 0, item.maximum ?? 1])
      .range([dimensions.boundedHeight, 0])
  }));

  const xAccessor = (d: any) => d.x;
  const yAccessor = (d: any) => d.y;

  return (
    <div ref={ref} style={{ width: "100%", height: "calc(100% - 10px)", marginBottom: 10 }} >
      <IconButton style={{ position: "absolute", right: 5, top: 65 }} onClick={() => setShowAxes(!showAxes)} size={"small"}>
        <Tooltip title={showAxes ? "Hide Axes" : "Show Axes"}>
          {showAxes
            ? <ArrowForwardIosIcon style={{ color: getPalette().statusColor.info.background }}/>
            : <ArrowBackIosIcon style={{ color: getPalette().statusColor.info.background }}/>
          }
        </Tooltip>
      </IconButton>

      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={lineID}
          dimensions={dimensions}
          scale={xScale}
          label={lineChartConfig.xAxis.name}
          ticks={getTicks(xScale.domain(), lineChartConfig.xAxis.ticksCount)}
          formatTick={lineChartConfig.xAxis.formatTick}
        />


        {showAxes && <g>
          {lineChartConfig && yScales.map((scale, index) => {
            const yAxis = lineChartConfig.yAxes[index];

            return (
              <AxisVertical
                chartID={lineID}
                key={`${lineID}-AxisVertical-${index}`}
                dimensions={dimensions}
                scale={scale.axis}
                label={yAxis.name}
                ticks={getTicks(scale.axis.domain(), lineChartConfig.xAxis.ticksCount)}
                formatTick={yAxis.formatTick}
                index={index}
                series={lineChartConfig.series.filter(s => s.yAxisName === scale.name)}/>
            );
          })}
        </g>}

        <Grid charID={lineID} xScale={xScale} yScale={yScales[0].axis} dimensions={dimensions}
          xTicks={getTicks(xScale.domain(), lineChartConfig.xAxis.ticksCount)}
          yTicks={getTicks(yScales[0].axis.domain(), lineChartConfig.yAxes[0].ticksCount)}/>

        <Legend chartID={lineID} series={lineChartConfig.series}  width={dimensions.boundedWidth}/>

        <g>
          {lineChartConfig && lineChartConfig.series.map((item, index) => {

            const yScale = yScales.find(y => y.name === item.yAxisName);
            if (!yScale) {
              return null;
            }

            const xAccessorScaled = (d: IPoint) => xScale(xAccessor(d));
            const yAccessorScaled = (d: IPoint) => yScale.axis(yAccessor(d));
            const lineGenerator = d3.line<IPoint>()
              .x(xAccessorScaled)
              .y(yAccessorScaled)
              .curve(d3.curveMonotoneX);

            const dataPoints = item.value as IPoint[];
            const key = `${lineID}-series-${index}`;

            return(
              <Fragment key={`${lineID}-series-${index}`}>
                {/*<circle cx={index * 150} cy={-20} r="5" stroke={item.series.color} fill={item.series.color} strokeWidth={2}/>*/}
                {/*<text className="Axis__tick" fontWeight="bold" fontSize="1em" x={index * 150 + 15} y={-15}>{item.series.name}</text>*/}

                <path
                  className={"Line"}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={2}
                  d={lineGenerator(dataPoints) ?? ""}
                />

                { item.limits && item.limits.map((limit, limitIndex) => {
                  const y = yScale.axis(limit.value);

                  return(
                    <g key={`${key}-limit-${limitIndex}`}>
                      <line  x1={0} x2={dimensions.boundedWidth} y1={y} y2={y} strokeDasharray="4"
                        stroke={limit.color} strokeWidth={2}/>

                      <text
                        stroke={limit.color}
                        className="Axis__tick"
                        x={2}
                        y={y - 3}
                        textAnchor={"start"}>
                        {limit.title}
                      </text>
                    </g>
                  );
                })
                }

                { dataPoints.map((point, pointIndex) => {
                  return(
                    <HtmlTooltip
                      key={`${key}-point-${pointIndex}-tooltip`}
                      title={
                        <div className="display-flex-column justify-content-end">
                          {lineChartConfig.xAxis.formatTick && <strong className={"align-self-center"}>{lineChartConfig.xAxis.formatTick(point.x ?? 0, 0)}</strong>}
                          <div className="d-flex display-flex-row">
                            <div style={{ width: 10, height: 10, borderRadius: 10, background: item.color, margin: 5 }} />
                            <KeyValueItem name={item.name} value={formatNumber((point as IPoint).y, 2)} unit={item.unit} />
                          </div>
                        </div>
                      }>
                      <circle cx={xAccessorScaled(point as IPoint)} cy={yAccessorScaled(point as IPoint)} r="1" fill="none" stroke={item.color} strokeWidth={1}/>
                    </HtmlTooltip>
                  );
                })}
              </Fragment>
            );
          })
          }
        </g>

      </Chart>
    </div>
  );
};

export default ProductTrends;
